/* eslint-disable max-len */
import { ContentService } from '@blk/cwp-ng-framework';
import { NumberUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';

import { InsightsTransform } from './insights-transform';

export class FiRiskInsightsTransform extends InsightsTransform {
	public overviewParagraph: string;
	public conclusionParagraph: string;
	private overviewParagraphDCR: string;
	private conclusionParagraphDCR: string;
	private fiInsightsRule1DCR: string;
	private fiInsightsRule2DCR: string;
	private fiInsightsRule3DCR: string;
	private fiInsightsRule4DCR: string;
	private fiInsightsRule5DCR: string;

	constructor(public contentSvc: ContentService) {
		super(contentSvc);
	}

	protected setupContent(): void {
		this.overviewParagraphDCR = this.contentSvc.get(
			'FI_INSIGHTS_OVERVIEW_DCR',
			'<p>Relative to its benchmark, the fixed income portfolio has a <b>{0} duration</b>, and a <b>{1} yield</b>.</p><p>This results is a <b>{2} total risk than its benchmark</b>, where {3} is interest rate risk, and {4} is credit spread risk.</p>'
		);
		this.conclusionParagraphDCR = this.contentSvc.get(
			'FI_INSIGHTS_CONCLUSION_DCR',
			'<p>{0}</p><p><b>Does the mix of risk in the fixed income portfolio match its role in the total portfolio?</b></p>'
		);

		this.fiInsightsRule1DCR = this.contentSvc.get(
			'FI_INSIGHTS_RULE_1',
			'With low volatility, the fixed income portfolio is <b>more likely to provide stability, than income or equity diversification.</b>'
		);
		this.fiInsightsRule2DCR = this.contentSvc.get(
			'FI_INSIGHTS_RULE_2',
			'With moderate volatility and high interest rate risk, the fixed income portfolio is <b>more likely to provide diversification, than income or stability.</b>'
		);
		this.fiInsightsRule3DCR = this.contentSvc.get(
			'FI_INSIGHTS_RULE_3',
			'With moderate volatility and low interest rate risk, the fixed income portfolio is <b>more likely to provide income, than stability or equity diversification.</b>'
		);
		this.fiInsightsRule4DCR = this.contentSvc.get(
			'FI_INSIGHTS_RULE_4',
			'With high volatility and high interest rate risk, the fixed income portfolio is <b>more likely to provide diversification, than income or stability.</b>'
		);
		this.fiInsightsRule5DCR = this.contentSvc.get(
			'FI_INSIGHTS_RULE_5',
			'With high volatility and low interest rate risk, the fixed income portfolio is <b>more likely to provide income, than stability or equity diversification.</b>'
		);
	}

	protected getEvaluatedOverview(selectedPortfolioIndex: number): string {
		const yieldValue = get(this.insightsTab, 'tableData[0].cells[' + selectedPortfolioIndex + '][0].rawValue');
		const duration = get(this.insightsTab, 'tableData[0].cells[' + selectedPortfolioIndex + '][1].rawValue');
		const totalRisk = get(this.insightsTab, 'tableData[0].cells[' + selectedPortfolioIndex + '][2].rawValue');

		const benchmarkYield = get(this.insightsTab, 'tableData[0].cells[' + this.benchmarkIndex + '][0].rawValue');
		const benchmarkDuration = get(this.insightsTab, 'tableData[0].cells[' + this.benchmarkIndex + '][1].rawValue');
		const benchmarkTotalRisk = get(this.insightsTab, 'tableData[0].cells[' + this.benchmarkIndex + '][2].rawValue');

		const decompositionData = get(this.factorsTab, 'tableData[0]') || {};
		const ratesIndex = decompositionData.headersX && decompositionData.headersX.findIndex(header => header.rawValue === 'Rates');
		const rates = ratesIndex >= 0 ? get(decompositionData, 'cells[' + selectedPortfolioIndex + '][' + ratesIndex + '].rawValue') : null;
		const spreadsIndex = decompositionData.headersX && decompositionData.headersX.findIndex(header => header.rawValue === 'Spreads');
		const spreads = spreadsIndex >= 0 ? get(decompositionData, 'cells[' + selectedPortfolioIndex + '][' + spreadsIndex + '].rawValue') : null;
		const interestRateRisk = NumberUtil.formatNumber(rates / totalRisk * 100);
		const creditSpreadRisk = NumberUtil.formatNumber(spreads / totalRisk * 100);

		return isNaN(+interestRateRisk) || isNaN(+creditSpreadRisk)
			? null
			: this.overviewParagraphDCR
				.replace('{0}', this.getCompareDurationDescription(duration, benchmarkDuration, 10))
				.replace('{1}', this.getCompareNumberDescription(yieldValue, benchmarkYield, 10))
				.replace('{2}', this.getCompareNumberDescription(totalRisk, benchmarkTotalRisk, 5))
				.replace('{3}', interestRateRisk + '%')
				.replace('{4}', creditSpreadRisk + '%');
	}

	protected getEvaluatedConclusion(selectedPortfolioIndex: number): string {
		const totalRisk = get(this.insightsTab, 'tableData[0].cells[' + selectedPortfolioIndex + '][' + 2 + '].rawValue');
		const decompositionData = get(this.factorsTab, 'tableData[0]') || {};
		const ratesIndex = decompositionData.headersY && decompositionData.headersX.findIndex(header => header.rawValue === 'Rates');
		const rates = ratesIndex >= 0 ? get(decompositionData, 'cells[' + selectedPortfolioIndex + '][' + ratesIndex + '].rawValue') : null;
		const ratesPercentage = rates / totalRisk * 100;

		if (totalRisk || totalRisk === 0) {
			let conclusion;
			if (totalRisk <= 2) {
				conclusion = this.fiInsightsRule1DCR;
			} else if (totalRisk > 2 && totalRisk < 3) {
				if (ratesPercentage >= 60) {
					conclusion = this.fiInsightsRule2DCR;
				} else {
					conclusion = this.fiInsightsRule3DCR;
				}
			} else {
				if (ratesPercentage >= 60) {
					conclusion = this.fiInsightsRule4DCR;
				} else {
					conclusion = this.fiInsightsRule5DCR;
				}
			}
			return this.conclusionParagraphDCR.replace('{0}', conclusion);
		} else {
			return null;
		}
	}
}
